"use client";

import { Children, T_Html } from '@/types/app.type';
import { ComponentProps, Fragment, forwardRef } from 'react';
import { Box } from './Box';

export type StackProps = Omit<ComponentProps<typeof Box>, 'size'> & {
    children: Children[];
    divider?: Children;
} & (
    | { gap: number; fluid?: never }
    | { fluid: boolean; gap?: never }
);

export const Stack = forwardRef<HTMLElement, StackProps>((props, ref) => {
    const { children, fluid, gap, divider, ...boxProps } = props;

    const content = children.filter((child: T_Html) => !!child);

    const commonProps = {
        ...boxProps,
        ref,
        height: fluid && boxProps.column ? '100%' : boxProps.height,
        width: fluid && !boxProps.column ? '100%' : boxProps.width,
    };

    if (process.env.NODE_ENV !== 'production' && content.length < 2) {
        console.warn('Stack requires at least two children. Use Box for single-child layouts.');
    }

    const style = {
        gap: divider ? (gap ? gap * 0.5 : undefined) : gap,
        justifyContent: fluid ? 'space-between' : undefined,
        ...boxProps.style,
    };

    return (
        <Box {...commonProps} style={style}>
            {!divider
                ? content
                : content.map((child: HTMLElement, index: number) => (
                    <Fragment key={index}>
                        {child}
                        {index < content.length - 1 && divider}
                    </Fragment>
                ))}
        </Box>
    );
});

Stack.displayName = 'Stack';
